import { useCallback, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Button, CircularProgress, Dialog, IconButton, Stack, styled, Typography } from '@mui/material';
import { ArrowBackIosNew, Edit } from '@mui/icons-material';

import { projectsAtom } from '@/interfaces/group';
import { selectedDeviceAtom } from '@/components/common/MapView';
import { projectSensorsWithDataAtom } from '@/interfaces/project';
import DeviceCard from '@/components/devices/DeviceCard';
import ManageDevicesDialog from '@/components/projects/ManageDevicesDialog';
import EditProjectName from '@/components/projects/EditProjectName';

const InfoText = styled(Typography)`
  &, & *{
    font-size: 18px;
  }
`;

export default function ProjectDetail() {
  const { data: projects, refetch: refetchProjects } = useAtomValue(projectsAtom);
  const { data: projectSensors, refetch: refetchSensors } = useAtomValue(projectSensorsWithDataAtom);
  const setSelectedDevice = useSetAtom(selectedDeviceAtom);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [dialog, setDialog] = useState('');
  const { t, i18n } = useTranslation();

  const isKorean = i18n.language.startsWith('ko');
  const dateFormat = isKorean ? 'yyyy.MM.dd a hh:mm' : 'MM/dd/yyyy hh:mm a';
  const projectId = searchParams.get('projectId') ?? '';
  const project = projects?.find((project) => project.id === projectId);

  function onBackClick() {
    navigate('/projects');
    setSelectedDevice(null);
  }
  const onDialogClose = useCallback(
    (reason?: string) => {
      setDialog('');

      if (reason === 'updated') {
        refetchProjects();
        refetchSensors();
      }
    },
    [refetchProjects, refetchSensors],
  );

  if (projectId === '' || !project) return <Navigate to="/projects" />;
  return (
    <>
      <Stack direction="row" alignItems="center" gap={1}>
        <IconButton onClick={onBackClick}>
          <ArrowBackIosNew />
        </IconButton>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: '-2px' }}>
          {project.name}
        </Typography>
        <IconButton onClick={() => setDialog('EDIT_NAME')} sx={{ mr: 'auto' }}>
          <Edit />
        </IconButton>
        <Button variant="contained" onClick={() => setDialog('MANAGE_DEVICE')}>
          {t('Common.label.manageDevices')}
        </Button>
      </Stack>

      <Stack gap={1}>
        <InfoText>
          <Typography component="span" fontWeight="bold">
            {t('Common.label.createdOn')}:&nbsp;
          </Typography>
          {format(project.createdAt, dateFormat)}
        </InfoText>
      </Stack>

      {projectSensors ? (
        <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="space-between">
          {projectSensors.map((sensor) => {
            return <DeviceCard key={sensor.id} device={sensor} />;
          })}
        </Stack>
      ) : (
        <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
          <CircularProgress />
        </Stack>
      )}

      <Dialog open={dialog === 'MANAGE_DEVICE'} onClose={onDialogClose}>
        <ManageDevicesDialog projectName={project.name} onClose={onDialogClose} />
      </Dialog>
      <Dialog open={dialog === 'EDIT_NAME'} onClose={onDialogClose}>
        <EditProjectName initialValue={project.name} onClose={onDialogClose} />
      </Dialog>
    </>
  );
}
